import { supabase } from "@/infrastructure/supabase/client";
import type {
  DeliveryTaskRow,
  Priority,
  TaskStatus,
} from "@/infrastructure/supabase/database.types";

export interface DeliveryTaskFilters {
  status?: TaskStatus;
  priority?: Priority;
  assigneeId?: string;
  workstreamKey?: string;
  sprintId?: string;
}

/**
 * Everything except the title and the caller is optional — a task can be filed
 * with just a name and sorted into a sprint or block later.
 */
export type CreateDeliveryTaskInput = Pick<DeliveryTaskRow, "title" | "created_by"> &
  Partial<
    Pick<
      DeliveryTaskRow,
      | "description"
      | "status"
      | "priority"
      | "sprint_id"
      | "workstream_key"
      | "assignee_id"
      | "due_date"
    >
  >;

/**
 * The editable surface of a task. Deliberately excludes `id`, `created_by` and
 * the timestamps: the insert policy pins `created_by` to auth.uid(), but the
 * update policy only checks `auth_is_admin()`, so nothing in the database stops
 * an admin from rewriting authorship — the type is what keeps it honest.
 */
export type DeliveryTaskPatch = Partial<
  Pick<
    DeliveryTaskRow,
    | "title"
    | "description"
    | "status"
    | "priority"
    | "sprint_id"
    | "workstream_key"
    | "assignee_id"
    | "due_date"
  >
>;

export const deliveryTasksRepository = {
  async list(filters: DeliveryTaskFilters = {}): Promise<DeliveryTaskRow[]> {
    let q = supabase
      .from("delivery_tasks")
      .select("*")
      .order("due_date", { ascending: true, nullsFirst: false })
      .order("created_at", { ascending: false });
    if (filters.status) q = q.eq("status", filters.status);
    if (filters.priority) q = q.eq("priority", filters.priority);
    if (filters.assigneeId) q = q.eq("assignee_id", filters.assigneeId);
    if (filters.workstreamKey) q = q.eq("workstream_key", filters.workstreamKey);
    if (filters.sprintId) q = q.eq("sprint_id", filters.sprintId);
    const { data, error } = await q;
    if (error) throw error;
    return data ?? [];
  },

  async create(input: CreateDeliveryTaskInput): Promise<DeliveryTaskRow> {
    // Explicit payload: `created_by` must be the caller's auth.uid() or the
    // insert policy rejects the row (migration 0129).
    const payload = {
      title: input.title,
      description: input.description ?? null,
      status: input.status ?? "todo",
      priority: input.priority ?? "medium",
      sprint_id: input.sprint_id ?? null,
      workstream_key: input.workstream_key ?? null,
      assignee_id: input.assignee_id ?? null,
      due_date: input.due_date ?? null,
      created_by: input.created_by,
    };
    const { data, error } = await supabase
      .from("delivery_tasks")
      .insert(payload)
      .select("*")
      .single();
    if (error) throw error;
    return data;
  },

  async update(id: string, patch: DeliveryTaskPatch): Promise<DeliveryTaskRow> {
    const { data, error } = await supabase
      .from("delivery_tasks")
      .update(patch)
      .eq("id", id)
      .select("*")
      .single();
    if (error) throw error;
    return data;
  },

  async remove(id: string): Promise<void> {
    const { error } = await supabase.from("delivery_tasks").delete().eq("id", id);
    if (error) throw error;
  },
};
